import React from 'react';
import {Atom, Plugins, AtomRenderProps} from '@packages/systems/wfdl';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {paginationButtonInitialStyle} from './PaginationNodeUtils';
import {SHARED_ANCHOR_NODE_CONSTRAINTS_DESCENDANTS} from '@packages/systems/core/constants/SharedConstraintRules';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {setIn} from '@packages/systems/core/utils/ImmUtils';
import {TListOfElement} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {getChildren} from '@packages/utilities/data-utils';

const setDisplayNone = setIn(['style', 'display'], 'none');

const {DESIGNER, SSR} = WebflowEnvironments;

// @ts-expect-error - TS7006 - Parameter 'ariaLabel' implicitly has an 'any' type.
const maybeAddAriaLabel = (ariaLabel) => {
  return ariaLabel ? ariaLabel : 'Previous Page';
};

const PaginationPrevious = Atom.createRender(
  ({
    context,
    generateAttributes,
    data,
    renderChild,
    attributes,
    classes,
  }: AtomRenderProps) => {
    const children = getChildren(data);
    const runtimeEnvironment = useRuntimeEnvironment();
    const href = context.get('paginationPreviousHref');
    const newAttributes = attributes
      .set('href', href || '#')
      .update('aria-label', maybeAddAriaLabel);

    if (href) {
      return (
        <a {...generateAttributes(newAttributes, classes)}>
          {children.map((child) => renderChild(child, context))}
        </a>
      );
    }

    // If the href is not defined, it means we are on the first pagination page. In that case, we don't want
    // to display the Previous button on the page. However, we need to render the button a bit differently
    // depending on the rendering environment we are in.
    switch (runtimeEnvironment) {
      case DESIGNER: {
        // In the Designer, we want to render the button with display none, so that the Designer canvas
        // would still contain the native DOM node, which is needed for the Designer UI and drag functionality.
        return (
          <a {...generateAttributes(setDisplayNone(newAttributes), classes)}>
            {children.map((child) => renderChild(child, context))}
          </a>
        );
      }
      case SSR: {
        // In the live site, we don't render anything so that the HTML would not contain anything extra.
        return null;
      }
      default: {
        // In all the other cases, i.e. in EXPORT, we want to render the buttons even when the href is not
        // defined so that the exported HTML contains both the Previous and Next buttons.
        return (
          <a {...generateAttributes(newAttributes, classes)}>
            {children.map((child) => renderChild(child, context))}
          </a>
        );
      }
    }
  }
);

const config = createIntegratedAtom({
  displayName: 'Previous',
  className: 'w-pagination-previous',
  style: paginationButtonInitialStyle,
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Link')),
  },
  showEmpty: false,
  render: PaginationPrevious,
  constraints: {
    ancestors: [{is: 'Pagination', rule: 'ExactlyOne'}],
    descendants: SHARED_ANCHOR_NODE_CONSTRAINTS_DESCENDANTS,
  },
  fields: {children: TListOfElement},
  // Add the native ID path in attributes for Analyze tracking
  autoEngagementTrackingAttribute: true,
});

export default config;
